import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { useLocale } from '@adsk/alloy-react-helpers';
import { isUndefined } from 'lodash';
import {
  useCurrentProductId,
  useCurrentProjectId,
  useCurrentAccountId,
  useCurrentProject,
  useCurrentAccount,
  PRODUCT_IDS,
} from '@adsk/alloy-provider';
import Illustration from '@adsk/alloy-react-illustration';
import { LinkButton } from '@adsk/alloy-react-button';
import { useStore } from '@common/StoreProvider';
import ProductsCategory from '../ProductsCategory';
import {
  productsLabelStyle,
  cantAddMembersTextStyle,
  inviteToProductsLabelStyle,
} from './styles';
import { fieldStyle } from '../../styles';
import { ACTIONS } from '../../state/actions';
import { getInitialProductAccess } from '../../utils';

const ProductsAccesses = ({ products, showProductsSectionRedesign }) => {
  const { state, dispatch } = useStore();
  const { T } = useLocale(({ locale }) => require(`../../i18n/${locale}.json`));
  const currentProjectId = useCurrentProjectId();
  const currentAccountId = useCurrentAccountId();
  const currentProductId = useCurrentProductId();
  const currentProduct = products.find(
    (p) => p.type === currentProductId && p.userAssignable
  );
  const filteredProducts = products.filter(
    (p) =>
      (p.userAssignable ||
        (showProductsSectionRedesign && p.type === PRODUCT_IDS.INSIGHT)) &&
      p.type !== currentProductId
  );
  const sortedProducts = [
    ...(currentProduct ? [currentProduct] : []),
    ...filteredProducts,
  ];
  const purchasedProducts = sortedProducts.filter((e) => e.included);
  const notPurchasedProducts = sortedProducts.filter((e) => !e.included);
  const currentProject = useCurrentProject();
  const currentAccount = useCurrentAccount();
  const accountName = isUndefined(currentAccount)
    ? 'Global Construction'
    : currentAccount.account_display_name;
  const isProjectAdmin = currentProject?.accessLevels?.projectAdministrator;
  const isAccountAdmin = currentProject?.accessLevels?.accountAdministrator;
  let productsSubscriptionTextKey = 'PRODUCTS_SUBSCRIPTION_INFO_PA';
  let notPurchasedProductsTooltip = T.translate(
    'NOT_PURCHASED_PRODUCTS_TOOLTIP'
  );

  if (isAccountAdmin) {
    productsSubscriptionTextKey = 'PRODUCTS_SUBSCRIPTION_INFO_AA';
  }
  if (showProductsSectionRedesign) {
    if (purchasedProducts.length > 0) {
      notPurchasedProductsTooltip = T.translate(
        'SOME_PURCHASED_PRODUCTS_TOOLTIP',
        {
          accountName,
        }
      );
    } else {
      notPurchasedProductsTooltip = T.translate(
        'NO_PURCHASED_PRODUCTS_TOOLTIP',
        {
          accountName,
        }
      );
    }
  }

  const handleProductAccessChange = (access) => {
    const { productId, value } = access;
    let newProductsAccesses = [];

    if (value) {
      newProductsAccesses = [productId, ...state.productsAccesses];
    } else {
      newProductsAccesses = state.productsAccesses.filter(
        (id) => id !== productId
      );
    }

    dispatch({
      type: ACTIONS.SET_PRODUCTS_ACCESSES,
      payload: newProductsAccesses,
    });
  };

  useEffect(() => {
    const isCurrentProductCondition =
      !currentProductId ||
      !products?.length ||
      state.productsAccesses.includes(PRODUCT_IDS.DOCS);

    if (isCurrentProductCondition) return;

    dispatch({
      type: ACTIONS.SET_PRODUCTS_ACCESSES,
      payload: getInitialProductAccess(currentProductId, products),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentProductId, products]);

  return (
    <div css={fieldStyle} data-testid="InviteProjectMemberModal_products_field">
      {!showProductsSectionRedesign && (
        <div css={productsLabelStyle}>
          {T.translate('PRODUCT_ACCESS_LABEL_OLD')}
        </div>
      )}
      {showProductsSectionRedesign &&
        (isAccountAdmin ||
          (isProjectAdmin && purchasedProducts.length > 0)) && (
          <>
            <div css={inviteToProductsLabelStyle}>
              {T.translate('PRODUCT_ACCESS_LABEL')}
            </div>
            <div>
              {T.translate(productsSubscriptionTextKey, {
                LearnMoreLink: (
                  <LinkButton>
                    <a
                      css={{ textDecoration: 'none' }}
                      href="https://help.autodesk.com/view/DOCS/ENU/?guid=Product_Access_And_Subscriptions"
                      target="_blank"
                      rel="noreferrer"
                    >
                      {T.translate('LEARN_MORE')}
                    </a>
                  </LinkButton>
                ),
              })}
            </div>
          </>
        )}
      {(!showProductsSectionRedesign ||
        (showProductsSectionRedesign && purchasedProducts.length > 0)) && (
        <ProductsCategory
          products={purchasedProducts}
          labelText={
            showProductsSectionRedesign
              ? null
              : T.translate('PURCHASED_PRODUCTS')
          }
          tooltipText={T.translate('PURCHASED_PRODUCTS_TOOLTIP')}
          onProductSelected={handleProductAccessChange}
          noProductsText={T.translate('NO_PURCHASED_PRODUCTS')}
          style={
            showProductsSectionRedesign
              ? { paddingTop: '16px' }
              : { paddingTop: '10px' }
          }
        />
      )}
      {(!showProductsSectionRedesign ||
        (showProductsSectionRedesign &&
          isAccountAdmin &&
          notPurchasedProducts.length > 0)) && (
        <ProductsCategory
          products={notPurchasedProducts}
          labelText={
            showProductsSectionRedesign
              ? T.translate('UNPURCHASED_PRODUCTS')
              : T.translate('NOT_PURCHASED_YET')
          }
          tooltipText={notPurchasedProductsTooltip}
          onProductSelected={handleProductAccessChange}
          style={{ paddingTop: '16px' }}
        />
      )}
      {showProductsSectionRedesign &&
        !isAccountAdmin &&
        purchasedProducts.length === 0 && (
          <div css={{ textAlign: 'center' }}>
            <Illustration
              height="144px"
              style={{
                margin: '22px 0 35px 0',
              }}
              type="peopleUpperBodyGrey"
              width="150px"
            />
            <div css={cantAddMembersTextStyle}>
              {T.translate('NO_SUBSCRIBED_PRODUCTS_TITLE')}
            </div>
            <div>
              {T.translate('NO_SUBSCRIBED_PRODUCTS_INFO', {
                accountName,
                StartTrialLink: (
                  <LinkButton>
                    <a
                      css={{ textDecoration: 'none' }}
                      href={`http://acc.autodesk.com/access/start-trial/accounts/${currentAccountId}/projects/${currentProjectId}?pk=docs&key=TRIAL-BM36DP`}
                      target="_blank"
                      rel="noreferrer"
                    >
                      {T.translate('START_TRIAL')}
                    </a>
                  </LinkButton>
                ),
              })}
            </div>
          </div>
        )}
    </div>
  );
};

ProductsAccesses.propTypes = {
  products: PropTypes.arrayOf(
    PropTypes.shape({
      type: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      included: PropTypes.bool.isRequired,
      userAssignable: PropTypes.bool.isRequired,
    })
  ).isRequired,
  showProductsSectionRedesign: PropTypes.bool.isRequired,
};

export default ProductsAccesses;
